/**
 * db-volume-tiers.js (Daily Bloom, ADR-0002 db- prefix)
 * Spec: specs/db-volume-tiers-widget.md (requirements locked 2026-07-02).
 *
 * Behavior for the <db-volume-tiers> PDP widget rendered by
 * snippets/db-volume-tiers.liquid. Purely visual: selecting a tier box sets
 * the product form quantity and mirrors the box's prices into the ATC button
 * price span (discounted total on data-button-price, struck full total on
 * data-button-compare, both Liquid money-formatted). The native
 * automatic-discount ladder applies at checkout; nothing here touches
 * pricing.
 *
 * The wrapper's inner HTML is replaced on variant change via the
 * getSectionsToRender() path in product.js, which also resets the quantity
 * input, so click listeners live on the custom element itself (survives the
 * swap) and no re-init is needed.
 */
if (!customElements.get('db-volume-tiers')) {
  class DBVolumeTiers extends HTMLElement {
    constructor() {
      super();
      this.syncing = false;
      this.addEventListener('click', this.onClick.bind(this));
      // Keep box selection honest when the shopper uses the native +/- stepper
      // instead of the boxes. Delegated on document because the quantity
      // selector is re-rendered (replaced) on variant change.
      document.addEventListener('change', (e) => {
        if (this.syncing) return;
        if (e.target && e.target.id === `Quantity-${this.dataset.section}`) {
          this.syncFromQuantity(parseInt(e.target.value, 10));
        }
      });
    }
    onClick(e) {
      const option = e.target.closest('.db-volume-tiers__option');
      if (!option || !this.contains(option)) return;
      this.selectOption(option);
      this.setQuantity(option);
      this.setButtonPrice(option);
    }
    selectOption(option) {
      this.querySelectorAll('.db-volume-tiers__option').forEach((el) => {
        const selected = el === option;
        el.classList.toggle('is-selected', selected);
        el.setAttribute('aria-checked', selected ? 'true' : 'false');
      });
    }
    getQuantityInput() {
      return document.getElementById(`Quantity-${this.dataset.section}`) || this.querySelector('input[name="quantity"]');
    }
    setQuantity(option) {
      const input = this.getQuantityInput();
      if (!input) return;
      let qty = parseInt(option.dataset.qty, 10) || 1;
      const max = parseInt(input.max, 10);
      if (max && qty > max) qty = max;
      this.syncing = true;
      input.value = qty;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this.syncing = false;
      const selector = input.closest('quantity-selector');
      if (selector && typeof selector.validateQtyRules === 'function') {
        selector.validateQtyRules();
      }
    }
    setButtonPrice(option) {
      const priceSpan = document.getElementById(`price-${this.dataset.section}--button`);
      if (!priceSpan || !option.dataset.buttonPrice) return;
      priceSpan.textContent = '';
      // Multi-item tiers mirror the box: struck full total + discounted total.
      // Both strings are Liquid money-formatted, carried on data attributes.
      if (option.dataset.buttonCompare && option.dataset.buttonCompare !== option.dataset.buttonPrice) {
        const compare = document.createElement('s');
        compare.textContent = option.dataset.buttonCompare;
        priceSpan.appendChild(compare);
        priceSpan.appendChild(document.createTextNode(' '));
      }
      priceSpan.appendChild(document.createTextNode(option.dataset.buttonPrice));
    }
    syncFromQuantity(qty) {
      let match = null;
      this.querySelectorAll('.db-volume-tiers__option').forEach((el) => {
        if (parseInt(el.dataset.qty, 10) === qty) match = el;
      });
      if (match) {
        this.selectOption(match);
        this.setButtonPrice(match);
      } else {
        // Manual quantity between tiers: no box matches, clear the selection
        // and put the ATC button price back to the unit price (the theme's
        // pre-widget behavior for manual quantity changes).
        this.querySelectorAll('.db-volume-tiers__option').forEach((el) => {
          el.classList.remove('is-selected');
          el.setAttribute('aria-checked', 'false');
        });
        const unitOption = this.querySelector('.db-volume-tiers__option[data-qty="1"]');
        if (unitOption) this.setButtonPrice(unitOption);
      }
    }
  }
  customElements.define('db-volume-tiers', DBVolumeTiers);
}
